class EstimateShipping extends HTMLElement {
	constructor() {
		super();
		this.config = {
			maxRetries: 3,
			retryDelay: 1000,
			initialDelay: 800,
			maxDelay: 3000
		};

		this.selectors = {
			zip: '#es-shipping-calc-zip',
			province: '#es-shipping-calc-province',
			button: '#es-shipping-calc-btn',
			alerts: {
				danger: '#es-shipping-calc-alert-danger',
				warning: '#es-shipping-calc-alert-warning',
				success: '#es-shipping-calc-alert-success'
			},
			iconTemplate: '#shipping-rate-icon',
			storeIconTemplate: '#store-pickup-icon'
		};

		this.isAlterMode = false;
		this.initializeComponent();
	}

	initializeComponent() {
		this.bindMethods();
		this.initElements();
		this.addEventListeners();
		this.style.display = 'block';
		this.toggleButtonState();
	}

	bindMethods() {
		['handleInput', 'handleBlur', 'handleEnterKey', 'generateShippingRates', 'toggleButtonState', 'handleFocus']
			.forEach(method => this[method] = this[method].bind(this));
	}

	initElements() {
		const componentId = this.id || 'default';
		const uniqueId = componentId.replace('es-shipping-calc-', '');
		
		const uniqueSelectors = {
			zip: `#es-shipping-calc-zip-${uniqueId}`,
			province: `#es-shipping-calc-province-${uniqueId}`,
			button: `#es-shipping-calc-btn-${uniqueId}`,
			alerts: {
				danger: `#es-shipping-calc-alert-danger-${uniqueId}`,
				warning: `#es-shipping-calc-alert-warning-${uniqueId}`,
				success: `#es-shipping-calc-alert-success-${uniqueId}`
			},
			iconTemplate: `#shipping-rate-icon-${uniqueId}`,
			storeIconTemplate: `#store-pickup-icon-${uniqueId}`
		};

		this.elements = {
			zip: this.querySelector(uniqueSelectors.zip),
			province: this.querySelector(uniqueSelectors.province),
			button: this.querySelector(uniqueSelectors.button),
			alerts: Object.entries(uniqueSelectors.alerts)
				.reduce((acc, [key, selector]) => ({
					...acc,
					[key]: this.querySelector(selector)
				}), {}),
			label: this.querySelector('.field__label'),
			iconTemplate: this.querySelector(uniqueSelectors.iconTemplate),
			storeIconTemplate: this.querySelector(uniqueSelectors.storeIconTemplate)
		};
	}

	addEventListeners() {
		if (!this.elements.zip || !this.elements.button) return;

		this.elements.zip.addEventListener('input', this.handleInput);
		this.elements.zip.addEventListener('blur', this.handleBlur);
		this.elements.zip.addEventListener('keypress', this.handleEnterKey);
		this.elements.zip.addEventListener('focus', this.handleFocus);
		this.elements.button.addEventListener('click', this.generateShippingRates);

		// Add listener for subscription selection changes
		this.addSubscriptionListeners();
	}

	toggleButtonState() {
		const isEmpty = !this.elements.zip.value.trim();
		this.elements.button.disabled = isEmpty;
		this.elements.button.classList.toggle('btn-disabled', isEmpty);

		const buttonTextSpan = this.elements.button.querySelector('.btn-primary__text');
		if (buttonTextSpan) {
			buttonTextSpan.textContent = this.isAlterMode ? 'Alterar CEP' : 'Calcular frete';
		}
	}

	handleInput(event) {
		event.target.value = this.formatCEP(event.target.value);
		if (!event.target.value.trim()) {
			this.clearAlerts();
			this.elements.province.value = '';
			this.isAlterMode = false;
		} else if (this.isAlterMode && event.target.value.replace(/\D/g, '').length === 8) {
			this.isAlterMode = false;
			this.clearAlerts();
		}
		this.toggleButtonState();
	}

	async handleBlur(event) {
		const state = await this.getStateFromZIP(event.target.value);
		this.elements.province.value = state;

		if (!event.target.value.trim()) {
			this.elements.label.classList.remove('is-hidden');
		}
	}

	handleEnterKey(event) {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.generateShippingRates();
		}
	}

	formatCEP(cep) {
		cep = cep.replace(/\D/g, '');
		return cep.length > 5 ? `${cep.substring(0, 5)}-${cep.substring(5)}` : cep;
	}

	async getStateFromZIP(cep) {
		try {
			cep = cep.replace(/\D/g, '');
			if (cep.length !== 8) return '';

			const cepRanges = {
				'AC': { start: 69900000, end: 69999999 },
				'AL': { start: 57000000, end: 57999999 },
				'AM': { start: 69000000, end: 69899999 },
				'AP': { start: 68900000, end: 68999999 },
				'BA': { start: 40000000, end: 48999999 },
				'CE': { start: 60000000, end: 63999999 },
				'DF': { start: 70000000, end: 72799999 },
				'ES': { start: 29000000, end: 29999999 },
				'GO': { start: 72800000, end: 76799999 },
				'MA': { start: 65000000, end: 65999999 },
				'MG': { start: 30000000, end: 39999999 },
				'MS': { start: 79000000, end: 79999999 },
				'MT': { start: 78000000, end: 78899999 },
				'PA': { start: 66000000, end: 68899999 },
				'PB': { start: 58000000, end: 58999999 },
				'PE': { start: 50000000, end: 56999999 },
				'PI': { start: 64000000, end: 64999999 },
				'PR': { start: 80000000, end: 87999999 },
				'RJ': { start: 20000000, end: 28999999 },
				'RN': { start: 59000000, end: 59999999 },
				'RO': { start: 76800000, end: 76999999 },
				'RR': { start: 69300000, end: 69399999 },
				'RS': { start: 90000000, end: 99999999 },
				'SC': { start: 88000000, end: 89999999 },
				'SE': { start: 49000000, end: 49999999 },
				'SP': { start: 1000000, end: 19999999 },
				'TO': { start: 77000000, end: 77999999 }
			};

			const cepNumber = parseInt(cep, 10);
			
			for (const [state, range] of Object.entries(cepRanges)) {
				if (cepNumber >= range.start && cepNumber <= range.end) {
					return state;
				}
			}

			return '';
		} catch {
			return '';
		}
	}

	clearAlerts() {
		Object.values(this.elements.alerts).forEach(alert => {
			alert.innerHTML = '';
			alert.setAttribute('hidden', 'hidden');
		});
	}

	showAlert(type, message, isList = false) {
		const alert = this.elements.alerts[type.toLowerCase()];
		const content = isList ? `<ul class="results">${message}</ul>` : `<p>${message}</p>`;
		alert.innerHTML = content;
		alert.removeAttribute('hidden');
	}

	toggleLoadingState(isLoading) {
		this.elements.button.classList.toggle('is-loading', isLoading);
		this.elements.button.disabled = isLoading;
	}

	async delay(ms) {
		await new Promise(resolve => setTimeout(resolve, ms));
	}

	getCurrentProductData() {
		const isProductPage = window.location.pathname.includes('/products/');
		const isCartPage = window.location.pathname.includes('/cart');
		const isCartDrawer = this.closest('.cart-drawer') !== null;
		const isMainCart = this.closest('.cart') !== null && !isCartDrawer;

		if (isCartDrawer || isMainCart) {
			return { useCart: true };
		}

		if (isProductPage) {
			const productForm = document.querySelector('form[action*="/cart/add"]');
			if (productForm) {
				const variantId = productForm.querySelector('select[name="id"], input[name="id"]')?.value;
				const quantity = productForm.querySelector('input[name="quantity"]')?.value || 1;
				return variantId ? { id: variantId, quantity: parseInt(quantity, 10) } : null;
			}
		}

		let cartItems = [];
		
		if (window.Shopify?.cart?.items) {
			cartItems = window.Shopify.cart.items;
		}
		else if (document.querySelector('meta[name="cart-items"]')) {
			try {
				const cartMeta = document.querySelector('meta[name="cart-items"]').content;
				cartItems = JSON.parse(cartMeta);
			} catch (e) {
				console.warn('Erro ao parsear meta cart-items:', e);
			}
		}
		else {
			const cartItemElements = document.querySelectorAll('[data-cart-item], .cart-item');
			if (cartItemElements.length > 0) {
				cartItems = Array.from(cartItemElements).map(item => ({
					variant_id: item.dataset.variantId || item.querySelector('[data-variant-id]')?.dataset.variantId,
					quantity: parseInt(item.dataset.quantity || item.querySelector('[data-quantity]')?.dataset.quantity || 1)
				})).filter(item => item.variant_id);
			}
		}

		if (cartItems.length > 0) {
			return { 
				id: cartItems[0].variant_id || cartItems[0].id, 
				quantity: cartItems[0].quantity,
				useCart: true 
			};
		}

		return null;
	}

	async fetchShippingRates(params) {
		let attempts = 0;
		let currentDelay = this.config.retryDelay;
		let lastError = null;

		while (attempts < this.config.maxRetries) {
			try {
				const response = await fetch(`/cart/async_shipping_rates.json?${params}`);
				const data = await response.json();

				if (data.shipping_rates?.length > 0 || data.error) {
					return { success: data };
				}

				lastError = new Error('Aguardando cálculo do frete...');
				await this.delay(currentDelay);

				currentDelay = Math.min(currentDelay * 1.5, this.config.maxDelay);
				attempts++;
			} catch (error) {
				lastError = error;
				console.warn('🔍 [Estimate Shipping] Erro na tentativa', attempts + 1, ':', error);
				await this.delay(currentDelay);
				currentDelay = Math.min(currentDelay * 1.5, this.config.maxDelay);
				attempts++;
			}
		}

		throw lastError || new Error('Não foi possível calcular o frete após várias tentativas');
	}

	async calculateShippingRates(zip, city, state) {
		const productData = this.getCurrentProductData();
		
		const params = new URLSearchParams({
			'shipping_address[zip]': zip,
			'shipping_address[country]': 'Brazil',
			'shipping_address[province]': state,
			'shipping_address[city]': city
		}).toString();

		if (productData && !productData.useCart) {
			return this.calculateWithProductSimulation(productData, params);
		}

		return this.calculateWithCurrentCart(params);
	}

	async calculateWithProductSimulation(productData, params) {
		try {
			const currentCart = await this.getCurrentCart();
			const productInCart = currentCart.items?.find(item => item.variant_id == productData.id);
			
			if (productInCart) {
				return this.calculateWithCurrentCart(params);
			}

			const tempCart = {
				items: [{ id: productData.id, quantity: productData.quantity }]
			};

			const cartResponse = await fetch('/cart/add.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(tempCart)
			});

			if (!cartResponse.ok) {
				throw new Error('Erro ao simular o carrinho');
			}

			const prepareResponse = await fetch(`/cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });

			if (!prepareResponse.ok) {
				const data = await prepareResponse.json();
				return { error: data };
			}

			await this.delay(this.config.initialDelay);
			const result = await this.fetchShippingRates(params);

			await this.removeProductFromCart(productData.id);

			return result;
		} catch (error) {
			console.error('🔍 [Estimate Shipping] Erro na simulação:', error);
			throw error;
		}
	}

	async removeProductFromCart(variantId) {
		try {
			const currentCart = await this.getCurrentCart();
			
			const updatedItems = currentCart.items
				.filter(item => item.variant_id != variantId)
				.map(item => ({
					id: item.variant_id,
					quantity: item.quantity
				}));

			await fetch('/cart/clear.js', { method: 'POST' });
			await this.delay(300);

			if (updatedItems.length > 0) {
				await fetch('/cart/add.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ items: updatedItems })
				});
			}

			await this.updateGlobalCart();
		} catch (error) {
			console.warn('🔍 [Estimate Shipping] Erro ao remover produto do carrinho:', error);
		}
	}

	async calculateWithCurrentCart(params) {
		try {
			const cartData = await this.getCurrentCart();
			
			if (!cartData.items || cartData.items.length === 0) {
				return { error: { message: 'Não foi possível calcular o frete. Adicione produtos ao carrinho primeiro.' } };
			}

			const prepareResponse = await fetch(`/cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });

			if (!prepareResponse.ok) {
				const data = await prepareResponse.json();
				return { error: data };
			}

			await this.delay(this.config.initialDelay);
			const result = await this.fetchShippingRates(params);

			return result;
		} catch (error) {
			return { error: { message: 'Erro ao calcular frete com o carrinho atual.' } };
		}
	}

	async getCurrentCart() {
		try {
			const response = await fetch('/cart.js');
			return await response.json();
		} catch (error) {
			console.warn('Erro ao obter carrinho atual:', error);
			return { items: [] };
		}
	}

	async updateGlobalCart() {
		try {
			if (window.Shopify && window.Shopify.cart) {
				const cartData = await this.getCurrentCart();
				window.Shopify.cart = cartData;
			}

			const cartUpdateEvent = new CustomEvent('cart:updated', {
				detail: { cart: await this.getCurrentCart() }
			});
			document.dispatchEvent(cartUpdateEvent);
		} catch (error) {
			console.warn('Erro ao atualizar carrinho global:', error);
		}
	}

	clearCalculator() {
		this.elements.zip.value = '';
		this.elements.province.value = '';
		this.clearAlerts();
		this.isAlterMode = false;
		this.toggleButtonState();
		this.elements.zip.focus();
		this.elements.label.classList.remove('is-hidden');
	}

	async getCityFromZip(zip) {
		try {
			const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
			const data = await response.json();
			return data.localidade;
		} catch (error) {
			console.warn('Erro ao obter cidade do CEP:', error);
			return '';
		}
	}

	async generateShippingRates() {
		// If in alter mode, clear and restart
		if (this.isAlterMode) {
			this.clearCalculator();
			return;
		}

		this.toggleLoadingState(true);
		this.clearAlerts();

		try {
			const zip = this.elements.zip.value.replace(/\D/g, '');

			if (zip.length !== 8) {
				this.showAlert('Danger', 'Por favor, digite um CEP válido');
				return;
			}

			const city = await this.getCityFromZip(zip);
			if (!city) {
				this.showAlert('Danger', 'CEP não encontrado');
				return;
			}

			const state = await this.getStateFromZIP(zip);
			if (!state) {
				this.showAlert('Danger', 'CEP não encontrado');
				return;
			}

			this.elements.province.value = state;
			
			const isOnlySubscription = this.checkIfOnlySubscription();
			
			if (isOnlySubscription) {
				this.showSubscriptionFreeShipping();
				this.isAlterMode = true;
				this.toggleButtonState();
				return;
			}
			const result = await this.calculateShippingRates(zip, city, state);

			if (result.error) {
				this.showAlert('Danger', this.formatErrorMessage(result.error), true);
				return;
			}

			const { shipping_rates = [] } = result.success;

			if (!shipping_rates.length) {
				this.showAlert('Warning', 'Nenhuma opção de frete encontrada para este CEP.');
				return;
			}

			const formatDeliveryTime = function (rate) {
				const [minDays, maxDays] = rate.delivery_days;

				if (minDays === maxDays) {
					return `Até ${minDays} dias úteis`;
				}

				return `Entre ${minDays} a ${maxDays} dias úteis`;
			}

			const allRatesHtml = `
				${shipping_rates
					.map(rate => `
						<li class="shipping-rate">
							<span class="shipping-rate__name"> 
								${this.elements.iconTemplate.innerHTML}
								${rate.presentment_name} - ${formatDeliveryTime(rate)}
							</span>
							<span class="shipping-rate__price">
								R$ ${rate.price.replace('.', ',')}
							</span>
						</li>
					`).join('')}
				`;

			this.showAlert('Success', allRatesHtml, true);
			this.isAlterMode = true;
			this.toggleButtonState();
		} catch (error) {
			this.showAlert('Danger', 'Ocorreu um erro ao calcular o frete. Tente novamente.');
		} finally {
			this.toggleLoadingState(false);
		}
	}

	formatErrorMessage(error) {
		if (!error || typeof error !== 'object') {
			return '<li>Erro ao processar o cálculo do frete</li>';
		}

		return Object.entries(error)
			.map(([key, value]) => `<li><strong>${key}</strong>: ${value}</strong></li>`)
			.join('');
	}

	handleFocus() {
		this.elements.label.classList.add('is-hidden');
	}

	checkIfOnlySubscription() {
		const isCartDrawer = this.closest('.cart-drawer') !== null;
		const isCartPage = window.location.pathname.includes('/cart');
		const isProductPage = window.location.pathname.includes('/products/');
		
		if (isCartDrawer || isCartPage) {
			const cartItems = document.querySelectorAll('.cart-item[data-variant-id]');
			for (const item of cartItems) {
				const itemText = item.textContent || item.innerText || '';
				if (!itemText.includes('Assinatura') && !itemText.includes('assinatura')) {
					return false;
				}
			}
			
			if (window.Shopify && window.Shopify.cart && window.Shopify.cart.items) {
				for (const item of window.Shopify.cart.items) {
					if (!item.title.includes('Assinatura') && !item.title.includes('assinatura')) {
						return false;
					}
				}
			}
			
		return cartItems.length > 0;
		}
		
		if (isProductPage) {
			const subscriptionInput = document.querySelector('input[name="type"][id="subscription"]:checked');
			return subscriptionInput !== null;
		}
		
		return false;
	}

	showSubscriptionFreeShipping() {
		const freeShippingHtml = `
			<li class="shipping-rate">
				<span class="shipping-rate__name"> 
					${this.elements.iconTemplate.innerHTML}
					Frete Grátis - Assinatura
				</span>
				<span class="shipping-rate__price">
					Grátis
				</span>
			</li>
		`;

		this.showAlert('Success', freeShippingHtml, true);
	}

	addSubscriptionListeners() {
		const subscriptionInputs = document.querySelectorAll('input[name="type"][id="subscription"], .subscription-selector input[type="radio"][id="subscription"]');
		subscriptionInputs.forEach(input => {
			input.addEventListener('change', () => {
				if (this.isAlterMode && this.elements.zip.value.trim()) {
					this.clearAlerts();
					this.isAlterMode = false;
					this.toggleButtonState();
					
					if (this.checkIfOnlySubscription()) {
						this.showSubscriptionFreeShipping();
						this.isAlterMode = true;
						this.toggleButtonState();
					}
				}
			});
		});

		const normalPurchaseInputs = document.querySelectorAll('input[name="type"][id="normal"], .subscription-selector input[type="radio"][id="normal"]');
		normalPurchaseInputs.forEach(input => {
			input.addEventListener('change', () => {
				if (this.isAlterMode && this.elements.zip.value.trim()) {
					this.clearAlerts();
					this.isAlterMode = false;
					this.toggleButtonState();
				}
			});
		});

		const cartSubscriptionInputs = document.querySelectorAll('.cart-item input[name="subscription"]');
		cartSubscriptionInputs.forEach(input => {
			input.addEventListener('change', () => {
				if (this.isAlterMode && this.elements.zip.value.trim()) {
					this.clearAlerts();
					this.isAlterMode = false;
					this.toggleButtonState();
					
					if (this.checkIfOnlySubscription()) {
						this.showSubscriptionFreeShipping();
						this.isAlterMode = true;
						this.toggleButtonState();
					}
				}
			});
		});
	}
}

customElements.define('estimate-shipping', EstimateShipping);
